import { useEffect, useRef } from 'react';

/**
 * Invoke `onRunSettled` once each time the entity's latest run goes from in
 * flight to settled.
 *
 * The transition is observed on server data (the run's status changes under
 * polling), not on a user event, which is why this is an effect.
 *
 * Deliberately keyed on the active flag alone, not the run id: a processing
 * run that auto-accepts an invoice triggers a follow-through workflow which
 * becomes the entity's latest run and is usually already complete by the next
 * poll. The run id changes and the flag drops in the same tick — the entity
 * was just rewritten, so that must still count as "settled".
 */
export function useRunSettledCallback(params: {
  isRunActive: boolean;
  onRunSettled?: () => void;
}): void {
  const { isRunActive, onRunSettled } = params;
  const wasActiveRef = useRef(false);
  const callbackRef = useRef(onRunSettled);
  callbackRef.current = onRunSettled;

  useEffect(() => {
    if (isRunActive) {
      wasActiveRef.current = true;
      return;
    }
    if (wasActiveRef.current) {
      wasActiveRef.current = false;
      callbackRef.current?.();
    }
  }, [isRunActive]);
}
